import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { acquisitionsApi, type AcqInvoice } from "@/lib/api/acquisitions";
import { FileText, Plus } from "lucide-react";

/** Aligns with Prisma `AcquisitionInvoiceStatus` (DRAFT | MATCHED | PAID | CANCELLED). */
const invoiceStatusMap: Record<
  string,
  { variant: "success" | "warning" | "info" | "danger" | "neutral"; label: string }
> = {
  DRAFT: { variant: "warning", label: "Draft" },
  MATCHED: { variant: "success", label: "Matched" },
  PAID: { variant: "info", label: "Paid" },
  CANCELLED: { variant: "danger", label: "Cancelled" },
};

export default function AcqInvoicesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorFromQuery = searchParams.get("vendorId");
  const [invoices, setInvoices] = useState<AcqInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const vendorId = vendorFromQuery || undefined;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await acquisitionsApi.acqInvoices.list({
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        vendorId,
        limit: 50,
      });
      setInvoices(res.invoices);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load invoices", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, vendorId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(n);

  const columns: DataTableColumn<AcqInvoice>[] = [
    {
      id: "invoiceNumber",
      header: "Invoice #",
      cell: (row) => (
        <button
          onClick={() => navigate(`/acquisitions/invoices/${row.id}`)}
          className="font-mono font-medium text-primary hover:underline"
        >
          {row.invoiceNumber}
        </button>
      ),
    },
    {
      id: "vendor",
      header: "Vendor",
      cell: (row) => <span className="text-sm">{row.vendor.name}</span>,
    },
    {
      id: "po",
      header: "PO",
      cell: (row) => (
        <span className="text-sm font-mono text-slate-500">
          {row.purchaseOrder?.orderNumber || "—"}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      id: "amount",
      header: "Total",
      cell: (row) => (
        <span className="text-sm tabular-nums font-medium">
          {formatCurrency(Number(row.totalAmount))}
        </span>
      ),
      width: "w-28",
      align: "right",
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => {
        const cfg = invoiceStatusMap[row.status] || {
          variant: "neutral" as const,
          label: row.status,
        };
        return <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>;
      },
      width: "w-24",
    },
    {
      id: "date",
      header: "Date",
      cell: (row) => (
        <span className="text-xs text-slate-500">
          {new Date(row.invoiceDate).toLocaleDateString()}
        </span>
      ),
      width: "w-24",
      hideOnMobile: true,
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Vendor Invoices"
        description="Manage and match acquisition invoices"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Vendor Invoices" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by invoice number..."
        actions={
          <Button onClick={() => navigate("/acquisitions/invoices/new")}>
            <Plus className="mr-2 h-4 w-4" />
            New Invoice
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {Object.entries(invoiceStatusMap).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {total} invoice{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={invoices}
        rowKey={(row) => row.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<FileText className="h-6 w-6 text-slate-400" />}
            title="No invoices yet"
            description="Record vendor invoices after receiving shipments."
            action={
              <Button onClick={() => navigate("/acquisitions/invoices/new")}>
                <Plus className="mr-2 h-4 w-4" />
                New Invoice
              </Button>
            }
          />
        }
      />
    </PageContainer>
  );
}

export { invoiceStatusMap };
